import React from 'react';
import { Outlet } from 'react-router-dom';
import {
    Autocomplete,
    TextField,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import useCancelEvent from '../../../../hooks/event-hooks/useCancelEvent';
import DrawerOverlay from '../../../../components/Drawer/DrawerOverlay';
import EventCancelDialog from '../../../../components/Drawer/EventCancelDialog';
import AlertDialog from '../../../../components/Dialog/AlertDialog';
import EventPublishDrawer from '../../../../components/Drawer/EventPublishDrawer';
import EventAction from './EventAction';
import ShowDoneIconOnSelect from '../../../../icons/ShowDoneIconOnSelect';
import useEvent from '../../../../hooks/event-hooks/useEvent';
import { useHandlePublish } from '../../manage/Event/manage-details/hooks/useHandlePublish';
import { Body1, Body2, H6 } from '../../../../themes/typography';
import '../../../../styles/components/MainContent.css';

function EventContent() {
    const {
        states,
        handlePublish,
        loading,
        isReadyToPublish,
        errors,
        openEventPublishDrawer,
        getEventStatus,
        handleFinalPublish,
        setOpenEventPublishDrawer,
        isEventPublishTermsAccepted,
        setIsEventPublishTermsAccepted,
        shouldDisablePauseButton,
        shouldShowPublishButton,
        handlePauseEvent,
        shouldShowPauseButton,
        shouldShowDeleteButton,
        handleCancelEvent,
        handleDeleteEvent,
        changeEventStatusChipColor,
        handlePreviewEvent,
        isEventStateProvided,
        selectedState,
        setSelectedState,
        isCommisionPaidByEndUserProvided,
        commisionFeesPaybleBy,
        setCommisionFeesPaybleBy,
        eventData,
        whichActionBtnClicked
    } = useHandlePublish();

    const { formik, openEventCancelDialog, setOpenEventCancelDialog } = useCancelEvent();

    const { isPaidTicketCreated, isCommissionPaidByEndUser } = useEvent();

    const eventPublishDrawerTitle =
        errors?.length > 0 ? 'Complete pending steps to publish:' : 'Publish Event';

    return (
        <React.Fragment>
            <section id="event-content-section">
                <EventAction
                    handlePublish={handlePublish}
                    loading={loading}
                    isReadyToPublish={isReadyToPublish}
                    getEventStatus={getEventStatus}
                    shouldDisablePauseButton={shouldDisablePauseButton}
                    shouldShowPublishButton={shouldShowPublishButton}
                    handlePauseEvent={handlePauseEvent}
                    shouldShowPauseButton={shouldShowPauseButton}
                    shouldShowDeleteButton={shouldShowDeleteButton}
                    handleCancelEvent={handleCancelEvent}
                    handleDeleteEvent={handleDeleteEvent}
                    changeEventStatusChipColor={changeEventStatusChipColor}
                    handlePreviewEvent={handlePreviewEvent}
                    whichActionBtnClicked={whichActionBtnClicked}
                    eventData={eventData}
                />

                <div className="switchable-event-content">
                    <Outlet />
                </div>
            </section>
            <DrawerOverlay
                onClick={handleFinalPublish}
                onClose={() => setOpenEventPublishDrawer(false)}
                disabled={!isEventPublishTermsAccepted || errors?.length > 0}
                // disabled={!isEventPublishTermsAccepted || commisionFeesPaybleBy === null || errors?.length > 0}
                open={openEventPublishDrawer}
                drawerTitle={eventPublishDrawerTitle}
                loading={loading}
            >
                <div className="drawer-publish-event-content">
                    <div className="drawer-publish-event-error-container">
                        {!errors ||
                            (errors.length === 0 && (
                                <React.Fragment>
                                    <Body1 fontWeight={500}>
                                        Congratulation on completing all the steps to publish your
                                        event! Now you can publish your event and make it live for
                                        the audience.
                                    </Body1>
                                </React.Fragment>
                            ))}
                        <div className="drawer-publish-event-error-list">
                            {errors &&
                                errors.length > 0 &&
                                errors.map((error, index) => (
                                    <div key={index} style={{ display: 'flex', gap: '0.5rem' }}>
                                        <Body2 fontWeight={500}>{index + 1}.</Body2>
                                        <Body2>{Object.values(error)[0]}</Body2>
                                    </div>
                                ))}
                        </div>

                        {!isEventStateProvided && (
                            <div className="drawer-publish-event-state-if-not-provided">
                                <Autocomplete
                                    options={states}
                                    value={selectedState}
                                    onChange={(event, newValue) => setSelectedState(newValue)}
                                    getOptionLabel={(option) => option.name}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Provide Event State" />
                                    )}
                                />
                            </div>
                        )}

                        {(isCommissionPaidByEndUser === 0 && isPaidTicketCreated) && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <H6>
                                    Select Commission Payable By
                                </H6>
                                <ToggleButtonGroup
                                    color="main"
                                    value={commisionFeesPaybleBy}
                                    exclusive
                                    onChange={(event, newValue) =>
                                        setCommisionFeesPaybleBy(newValue)
                                    }
                                    aria-label="Platform fees payable by"
                                    sx={{ gap: '1.5rem' }}
                                >
                                    <ToggleButton value={false}>
                                        Me
                                        {commisionFeesPaybleBy ===
                                            false && <ShowDoneIconOnSelect />}
                                    </ToggleButton>
                                    <ToggleButton value={true}>
                                        Buyer
                                        {commisionFeesPaybleBy ===
                                            true && <ShowDoneIconOnSelect />}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                        )}

                    </div>
                </div>
                <EventPublishDrawer
                    checked={isEventPublishTermsAccepted}
                    setChecked={setIsEventPublishTermsAccepted}
                />
            </DrawerOverlay>
            <AlertDialog
                open={openEventCancelDialog}
                secondaryBtnName="Not now"
                primaryBtnName="Cancel"
                dialogTitle="Want to cancel this event?"
                dialogContentSx={{ overflow: 'visible' }}
                onConfirm={() => formik.handleSubmit()}
                onCancel={() => setOpenEventCancelDialog(false)}
            >
                <EventCancelDialog formik={formik} />
            </AlertDialog>
        </React.Fragment>
    );
}

export default EventContent;
